import Tagify from "@yaireo/tagify";
import _ from "lodash";

const suggestionCount = 60;

export const MultipleSelectHook = {
  mounted() {
    renderInput(this.el, this);
  },
};

function renderInput(el, hook) {
  const input = el.querySelector("input");
  $(".tagify-prerender", el).hide();
  let initWhitelist = [];

  if (input.dataset.options) {
    initWhitelist = JSON.parse(input.dataset.options);
  }

  if (input.value) {
    initWhitelist = _.unionBy(initWhitelist, JSON.parse(input.value), "id");
    initWhitelist = _.reject(initWhitelist, _.isUndefined);
    initWhitelist = _.reject(initWhitelist, _.isEmpty);
  }

  let placeAbove;
  if (el.dataset.dropdownPlacement === "above") {
    placeAbove = true;
  }
  if (el.dataset.dropdownPlacement === "below") {
    placeAbove = false;
  }

  // rendering tagify component
  const tagify = new Tagify(input, {
    editTags: false,
    tagTextProp: "value",
    whitelist: initWhitelist,
    enforceWhitelist: true,
    dropdown: {
      enabled: 0,
      highlightFirst: true,
      maxItems: suggestionCount,
      placeAbove: placeAbove,
    },
    transformTag: transformTag,
    originalInputValueFormat: (value) => {
      if ("optionsPreserveValue" in el.dataset) {
        return JSON.stringify(value);
      }
      return value.map((v) => v.id).join(",");
    },
  });

  // to be able to call tagify methods later on
  input.tagify = tagify;

  // if phx-load-options-event attribute is given, the component will send
  // an event to the LiveView to fetch further options
  if (el.dataset.optionsPhxEvent) {
    const phxLoadEvent = el.dataset.optionsPhxEvent;
    const inputHandler = _.debounce((e) => {
      tagify.settings.whitelist.length = 0;
      tagify.loading(true).dropdown.hide.call(tagify);

      let payload = e.detail?.value ?? "";
      // pushEvent should send an object, please use this option.
      if (el.dataset.optionsPhxPayload) {
        payload = { [el.dataset.optionsPhxPayload]: payload };
      }

      if (el.dataset.optionsPhxTarget) {
        hook.pushEventTo(el.dataset.optionsPhxTarget, phxLoadEvent, payload);
      } else {
        hook.pushEvent(phxLoadEvent, payload);
      }

      return true;
    }, 500);

    tagify.on("input", inputHandler);

    hook.handleEvent(phxLoadEvent, (data) => {
      // timeout to workaround a Tagify race condition when the total load_event roundtrip
      // is a bit too fast
      setTimeout(() => {
        const receivedOptions = _.take(
          _.sortBy(JSON.parse(data.options), "value"),
          suggestionCount,
        );
        let newWhitelist = _.unionBy([tagify.value], receivedOptions, "id");
        newWhitelist = _.sortBy(newWhitelist, "value");
        newWhitelist = _.reject(newWhitelist, (i) => _.isUndefined(i) || _.isEmpty(i));
        tagify.settings.whitelist = newWhitelist;
        tagify.loading(false).dropdown.show.call(tagify);
      }, 100);
    });
  }

  // if data-can-be-disabled-by attribute is given, options
  // can be disabled by clicking on them (and it will disable all options sharing
  // the same attribute value)
  if (input.dataset.canBeDisabledBy) {
    const disableAttribute = input.dataset.canBeDisabledBy;

    tagify.on("click", (e) => {
      toggleTag(e.detail, disableAttribute);
    });

    if (el.dataset.enabledGroups) {
      const enabledGroups = JSON.parse(el.dataset.enabledGroups);

      for (const tagifyTag of tagify.getTagElms()) {
        const groupId = tagifyTag.__tagifyTagData[disableAttribute];
        const enabled = enabledGroups[groupId] || false;
        tagifyTag.__tagifyTagData.enabled = enabled;
        tagifyTag.classList.toggle("tag-disabled", !enabled);
      }

      tagify.updateValueByDOMTags();
    }
    return tagify;
  }

  function toggleTag({ tag, data, tagify }, disableAttribute) {
    if (tagify.state.editing) return;
    let listedTags = tagify.getTagElms();
    listedTags = listedTags.filter(
      (listedTag) =>
        listedTag.__tagifyTagData[disableAttribute] === tag.__tagifyTagData[disableAttribute],
    );
    for (const listedTag of listedTags) {
      listedTag.__tagifyTagData.enabled = !data.enabled;
      listedTag.classList.toggle("tag-disabled", data.enabled);
    }
    tagify.updateValueByDOMTags();
  }

  function transformTag(tagData) {
    if (tagData.color) {
      tagData.style = `--tag-bg:${tagData.color}; `;
      tagData.style += `--tag-hover:${adjust(tagData.color, -15)}`;
    }
  }

  // one liner to adjust color brightness
  function adjust(color, amount) {
    return `#${color
      .replace(/^#/, "")
      .replace(/../g, (color) =>
        `0${Math.min(255, Math.max(0, Number.parseInt(color, 16) + amount)).toString(16)}`.substr(
          -2,
        ),
      )}`;
  }
}
